import { createApiRef } from '@backstage/core-plugin-api';

import type {
  CreateOrderInput,
  CreateOrderResponse,
  GetOrderByIdResponse,
  GetOrdersParams,
  GetPaginatedOrdersResponse,
  UpdateOrderBackstageBodyInput,
  UpdateOrderResponse,
  RequestDeletionBodyInput,
  OrderResponse,
  CheckUriPrefixResponse,
  CheckExistingByCostCenterResponse,
} from './n8nmanager.api.objects';

/**
 * API reference for the N8N Instance Manager
 */
export const n8nManagerApiRef = createApiRef<N8nManagerApi>({
  id: 'plugin.aiecosystem-n8nmanager.api',
});

/**
 * API interface for communicating with the N8N Instance Manager backend.
 * Covers the subset of endpoints needed by the Backstage UI:
 *  - list orders (paginated)
 *  - get order by ID (with contacts + rejection/deletion info)
 *  - create a new order
 *  - update an existing order (Backstage-permitted fields only)
 */
export interface N8nManagerApi {
  /**
   * Retrieve a paginated list of orders the authenticated user has access to.
   */
  getOrders(params?: GetOrdersParams): Promise<GetPaginatedOrdersResponse>;

  /**
   * Retrieve full details of a single order, including contacts and optional
   * rejectionReason / deletionComment.
   */
  getOrderById(id: string): Promise<GetOrderByIdResponse>;

  /**
   * Create a new SANDBOX or REGULAR instance order.
   */
  createOrder(input: CreateOrderInput): Promise<CreateOrderResponse>;

  /**
   * Update permitted fields of an existing order.
   * Backstage users may only update: label, description,
   * organizationalUnit, costcenter.
   */
  updateOrder(
    id: string,
    input: UpdateOrderBackstageBodyInput,
  ): Promise<UpdateOrderResponse>;

  /**
   * Request deletion of an order.
   */
  requestDeletion(
    id: string,
    input: RequestDeletionBodyInput,
  ): Promise<OrderResponse>;

  /**
   * Check whether a URI prefix is unique and available for use when creating
   * a new REGULAR order. The sandbox prefix is always reported as unavailable.
   */
  validateUriPrefix(uriPrefix: string): Promise<CheckUriPrefixResponse>;

  /**
   * Check whether any non-SANDBOX orders already exist for a given cost center.
   * Used to inform users before ordering a new regular instance.
   */
  checkExistingByCostCenter(
    costCenter: string,
  ): Promise<CheckExistingByCostCenterResponse>;
}
